import React, { useState } from 'react';
import { Box, Typography, ButtonBase, Collapse } from '@mui/material';
import { Functions as FunctionsIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { tutorTokens } from '../../theme/tutorTheme';

// ─────────────────────────────────────────────────────────────────────────
// MathKeypad — grouped symbol palette that inserts at the caret, not the
// end of the string. Every key defines:
//   glyph   — what's shown on the button
//   label   — full accessible name (screen readers never get just "x²")
//   insert  — the literal text spliced into the input
//   caretBack — how many characters to move the caret back from the end of
//               the inserted text (so "√()"/"( )" land the cursor inside)
// ─────────────────────────────────────────────────────────────────────────

const GROUPS = [
  {
    name: 'Variables',
    keys: [
      { glyph: 'x', label: 'variable x', insert: 'x' },
      { glyph: 'y', label: 'variable y', insert: 'y' },
      { glyph: '( )', label: 'parentheses', insert: '()', caretBack: 1 },
      { glyph: '=', label: 'equals', insert: '=' },
    ],
  },
  {
    name: 'Operators',
    keys: [
      { glyph: '+', label: 'plus', insert: '+' },
      { glyph: '−', label: 'minus', insert: '−' },
      { glyph: '×', label: 'times', insert: '×' },
      { glyph: '÷', label: 'divided by', insert: '÷' },
      { glyph: '±', label: 'plus or minus', insert: '±' },
    ],
  },
  {
    name: 'Powers & roots',
    keys: [
      { glyph: 'x²', label: 'x squared', insert: '²' },
      { glyph: 'x³', label: 'x cubed', insert: '³' },
      { glyph: 'xⁿ', label: 'raised to the power of', insert: '^' },
      { glyph: '√', label: 'square root', insert: '√()', caretBack: 1 },
      { glyph: 'a/b', label: 'fraction', insert: '()/()', caretBack: 4 },
    ],
  },
  {
    name: 'Comparison',
    keys: [
      { glyph: '<', label: 'less than', insert: '<' },
      { glyph: '≤', label: 'less than or equal to', insert: '≤' },
      { glyph: '>', label: 'greater than', insert: '>' },
      { glyph: '≥', label: 'greater than or equal to', insert: '≥' },
      { glyph: '≠', label: 'not equal to', insert: '≠' },
    ],
  },
  {
    name: 'Other',
    keys: [
      { glyph: 'π', label: 'pi', insert: 'π' },
      { glyph: 'θ', label: 'theta', insert: 'θ' },
      { glyph: '∞', label: 'infinity', insert: '∞' },
      { glyph: '|x|', label: 'absolute value', insert: '||', caretBack: 1 },
    ],
  },
];

/** Splices `insert` into `value` at the given caret position; returns { next, caret }. */
export function insertAtCaret(value, caretPos, key) {
  const pos = caretPos == null ? value.length : caretPos;
  const next = value.slice(0, pos) + key.insert + value.slice(pos);
  const caret = pos + key.insert.length - (key.caretBack || 0);
  return { next, caret };
}

// The outer ButtonBase is the real touch target (44px minimum, WCAG
// 2.5.8) via transparent padding; the inner Box carries the compact
// visible chip at its original size, so the dense keypad's density is
// unchanged while the actual hit area is not.
const KeyButton = ({ keyDef, onPress, dense }) => {
  const visualSize = dense ? 34 : 40;
  return (
    <ButtonBase
      onClick={() => onPress(keyDef)}
      aria-label={keyDef.label}
      sx={{
        minWidth: 44,
        minHeight: 44,
        p: 0.5,
        borderRadius: `${tutorTokens.radiusSm}px`,
        '&:focus-visible': { outline: `2px solid ${tutorTokens.accent}`, outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          minWidth: visualSize,
          height: visualSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: `${tutorTokens.radiusSm}px`,
          border: `1px solid ${tutorTokens.border}`,
          backgroundColor: tutorTokens.surfaceRaised,
          color: tutorTokens.ink,
          fontFamily: tutorTokens.fontUI,
          fontSize: dense ? '0.85rem' : '0.95rem',
          fontWeight: 600,
          px: 1,
          transition: 'border-color 0.12s ease, background-color 0.12s ease, transform 0.12s ease',
          '.MuiButtonBase-root:hover &': { borderColor: tutorTokens.accent, backgroundColor: tutorTokens.accentLight },
          '.MuiButtonBase-root:active &': { transform: 'scale(0.94)' },
        }}
      >
        {keyDef.glyph}
      </Box>
    </ButtonBase>
  );
};

/**
 * `inputRef` — ref to the underlying <input>/<textarea> DOM node (MUI
 * TextField's inputRef, not the root ref) so caret position can be read and
 * restored. `value`/`onChange(nextValue)` are controlled like a normal field.
 */
const MathKeypad = ({ inputRef, value, onChange, dense = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);

  const press = (keyDef) => {
    const el = inputRef?.current;
    const caretPos = el ? el.selectionStart : value.length;
    const { next, caret } = insertAtCaret(value || '', caretPos, keyDef);
    onChange(next);
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    });
  };

  return (
    <Box>
      <ButtonBase
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: tutorTokens.inkSoft,
          ...tutorTokens.type.label,
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          '&:focus-visible': { outline: `2px solid ${tutorTokens.accent}`, outlineOffset: 2 },
        }}
      >
        <FunctionsIcon sx={{ fontSize: 15 }} />
        Math keypad
        <ExpandMoreIcon sx={{ fontSize: 16, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
      </ButtonBase>
      <Collapse in={open}>
        {/* width/maxWidth 100% + boxSizing:border-box + minWidth:0 on every
            flex item below: without that last one, flex's default
            min-width:auto stops a group from shrinking below its own
            content size, which is what was forcing this panel wider than
            its container at 375px instead of wrapping cleanly. */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            mt: 1,
            p: 1.25,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            borderRadius: `${tutorTokens.radiusMd}px`,
            backgroundColor: tutorTokens.surface,
            border: `1px solid ${tutorTokens.border}`,
          }}
        >
          {GROUPS.map((group) => (
            <Box key={group.name} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
              <Typography sx={{ ...tutorTokens.type.eyebrow, color: tutorTokens.inkFaint, textTransform: 'uppercase' }}>
                {group.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
                {group.keys.map((k) => (
                  <KeyButton key={k.glyph} keyDef={k} onPress={press} dense={dense} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default MathKeypad;
